import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  UserPlus,
  Pencil,
  Phone,
  Mail,
  MapPin,
  Home,
  Building2,
  Star,
  Tag as TagIcon,
  StickyNote,
  AlertTriangle,
  Loader2,
  Plus,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { customersApi, geoApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";

const inputClass =
  "w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";
const labelClass = "block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5";

const MEMBERSHIPS = ["", "Silver", "Gold", "Platinum"];
const MEMBERSHIP_STYLE = {
  Silver: "bg-slate-100 text-slate-700 border-slate-300",
  Gold: "bg-amber-50 text-amber-700 border-amber-300",
  Platinum: "bg-violet-50 text-violet-700 border-violet-300",
};
const PRESET_TAGS = ["VIP", "Membership", "Repeat", "Net-30", "Warranty"];

const EMPTY = {
  name: "",
  type: "Residential",
  phone: "",
  altPhone: "",
  email: "",
  altEmail: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  membership: "",
  note: "",
};

function emailValid(e) {
  return !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function fromCustomer(c) {
  return {
    name: c.name || "",
    type: c.type || "Residential",
    phone: c.phone || "",
    altPhone: c.altPhone || "",
    email: c.email || "",
    altEmail: c.altEmail || "",
    address: c.address || "",
    city: c.city || "",
    state: c.state || "",
    zip: c.zip || "",
    membership: c.membership || "",
    note: "",
  };
}

/**
 * Centered modal to create OR edit a customer. Admin/dispatcher only (gated by parent).
 * Pass `editCustomer` to switch to edit mode.
 */
export default function NewCustomerModal({
  open,
  onClose,
  existingCustomers = [],
  existingTags = [],
  editCustomer = null,
  onSaved,
}) {
  const isEdit = !!editCustomer;
  const [f, setF] = useState(EMPTY);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  // Address autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [loadingSug, setLoadingSug] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);

  useEffect(() => {
    if (open) {
      setF(editCustomer ? fromCustomer(editCustomer) : EMPTY);
      setTags(editCustomer ? [...(editCustomer.tags || [])] : []);
      setTagInput("");
      setSaving(false);
      setSuggestions([]);
      setShowSug(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editCustomer]);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const tagOptions = useMemo(() => {
    const merged = new Set([...PRESET_TAGS, ...existingTags.filter((t) => t && t !== "All")]);
    return Array.from(merged);
  }, [existingTags]);

  const toggleTag = (t) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const addCustomTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  };

  // ---- Address autocomplete (OSM Nominatim via backend proxy) ----
  const onAddressChange = (value) => {
    set({ address: value });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSug(false);
      return;
    }
    setLoadingSug(true);
    setShowSug(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await geoApi.autocomplete(value.trim());
        setSuggestions(results || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoadingSug(false);
      }
    }, 350);
  };

  const pickSuggestion = (s) => {
    set({
      address: s.address || f.address,
      city: s.city || "",
      state: s.state || "",
      zip: s.zip || "",
    });
    setShowSug(false);
    setSuggestions([]);
  };

  // Likely-duplicate detection (warn but allow); exclude the record being edited.
  const duplicate = useMemo(() => {
    const name = f.name.trim().toLowerCase();
    const phoneDigits = f.phone.replace(/[^0-9]/g, "");
    const email = f.email.trim().toLowerCase();
    if (!name && !phoneDigits && !email) return null;
    return existingCustomers.find((c) => {
      if (editCustomer && c.id === editCustomer.id) return false;
      const cEmail = (c.email || "").trim().toLowerCase();
      const cPhone = (c.phone || "").replace(/[^0-9]/g, "");
      const cName = (c.name || "").trim().toLowerCase();
      if (email && cEmail && email === cEmail) return true;
      if (name && phoneDigits && cName === name && cPhone === phoneDigits) return true;
      return false;
    });
  }, [f.name, f.phone, f.email, existingCustomers, editCustomer]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!f.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!emailValid(f.email.trim())) {
      toast.error("Enter a valid email address (or leave it blank)");
      return;
    }
    if (!emailValid(f.altEmail.trim())) {
      toast.error("Enter a valid alt email address (or leave it blank)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        type: f.type,
        phone: f.phone.trim(),
        altPhone: f.altPhone.trim(),
        email: f.email.trim(),
        altEmail: f.altEmail.trim(),
        address: f.address.trim(),
        city: f.city.trim(),
        state: f.state.trim(),
        zip: f.zip.trim(),
        tags,
        membership: f.membership,
      };

      if (isEdit) {
        const updated = await customersApi.update(editCustomer.id, payload);
        toast.success(`Customer "${updated.name}" updated`);
        onSaved && onSaved(updated, true);
        onClose && onClose();
        return;
      }

      let created = await customersApi.create(payload);
      if (f.note.trim()) {
        try {
          created = await customersApi.addNote(created.id, f.note.trim());
        } catch {
          // Note is best-effort; customer is already created.
        }
      }
      if (duplicate) {
        toast.warning(`Heads up — "${duplicate.name}" looks similar. New customer created anyway.`);
      } else {
        toast.success(`Customer "${created.name}" created`);
      }
      onSaved && onSaved(created, false);
      onClose && onClose();
    } catch (err) {
      console.error(err);
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6"
      onClick={onClose}
      data-testid="new-customer-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center">
              {isEdit ? (
                <Pencil className="h-4 w-4 text-white" strokeWidth={2.5} />
              ) : (
                <UserPlus className="h-4 w-4 text-white" strokeWidth={2.5} />
              )}
            </div>
            <div>
              <div className="text-[14px] font-semibold text-slate-900">
                {isEdit ? "Edit Customer" : "New Customer"}
              </div>
              <div className="text-[11px] text-slate-500">
                {isEdit
                  ? "Update this customer's contact, address and profile"
                  : "Add a residential or commercial customer profile"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            data-testid="new-customer-close"
            className="h-7 w-7 rounded hover:bg-white/60 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="new-customer-form">
          {/* Identity */}
          <section className="space-y-3">
            <div>
              <label className={labelClass}>Name *</label>
              <input
                value={f.name}
                onChange={(e) => set({ name: e.target.value })}
                data-testid="new-customer-name"
                placeholder="e.g. Sarah Thompson or Acme Restaurant Group"
                className={inputClass}
                autoFocus
              />
            </div>

            {/* Type toggle */}
            <div>
              <label className={labelClass}>Customer type</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "Residential", icon: Home },
                  { v: "Commercial", icon: Building2 },
                ].map(({ v, icon: Icon }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set({ type: v })}
                    data-testid={`new-customer-type-${v.toLowerCase()}`}
                    className={`h-10 rounded-lg border text-[13px] font-medium flex items-center justify-center gap-1.5 transition-colors ${
                      f.type === v
                        ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {v}
                  </button>
                ))}
              </div>
            </div>

            {/* Phone + alt phone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Phone *</label>
                <div className="relative">
                  <Phone className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    value={f.phone}
                    onChange={(e) => set({ phone: e.target.value })}
                    data-testid="new-customer-phone"
                    placeholder="(555) 123-4567"
                    className={`${inputClass} pl-9`}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Alt phone</label>
                <input
                  value={f.altPhone}
                  onChange={(e) => set({ altPhone: e.target.value })}
                  data-testid="new-customer-alt-phone"
                  placeholder="Optional"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Email + alt email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Email</label>
                <div className="relative">
                  <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    value={f.email}
                    onChange={(e) => set({ email: e.target.value })}
                    data-testid="new-customer-email"
                    placeholder="name@example.com"
                    className={`${inputClass} pl-9`}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Alt email</label>
                <div className="relative">
                  <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    value={f.altEmail}
                    onChange={(e) => set({ altEmail: e.target.value })}
                    data-testid="new-customer-alt-email"
                    placeholder="Optional second email"
                    className={`${inputClass} pl-9`}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Address with autocomplete */}
          <section className="space-y-3 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> Service address
            </div>
            <div className="relative">
              <input
                value={f.address}
                onChange={(e) => onAddressChange(e.target.value)}
                onFocus={() => {
                  if (suggestions.length) setShowSug(true);
                }}
                onBlur={() => {
                  blurRef.current = setTimeout(() => setShowSug(false), 150);
                }}
                data-testid="new-customer-address"
                placeholder="Start typing an address…"
                autoComplete="off"
                className={inputClass}
              />
              {showSug && (
                <div
                  className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
                  data-testid="new-customer-address-suggestions"
                  onMouseDown={(e) => {
                    // keep focus so onBlur doesn't close before click registers
                    e.preventDefault();
                    if (blurRef.current) clearTimeout(blurRef.current);
                  }}
                >
                  {loadingSug && (
                    <div className="px-3 py-2.5 text-[12.5px] text-slate-400 flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching addresses…
                    </div>
                  )}
                  {!loadingSug && suggestions.length === 0 && (
                    <div className="px-3 py-2.5 text-[12.5px] text-slate-400">No matches found</div>
                  )}
                  {!loadingSug &&
                    suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => pickSuggestion(s)}
                        data-testid={`new-customer-address-suggestion-${i}`}
                        className="w-full px-3 py-2 text-left hover:bg-emerald-50/70 transition-colors border-b last:border-b-0 border-slate-50 flex items-start gap-2"
                      >
                        <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <span className="text-[12.5px] text-slate-700 leading-snug">{s.label}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-6 gap-3">
              <input
                value={f.city}
                onChange={(e) => set({ city: e.target.value })}
                data-testid="new-customer-city"
                placeholder="City"
                className={`${inputClass} col-span-3`}
              />
              <input
                value={f.state}
                onChange={(e) => set({ state: e.target.value })}
                data-testid="new-customer-state"
                placeholder="State"
                className={`${inputClass} col-span-1`}
              />
              <input
                value={f.zip}
                onChange={(e) => set({ zip: e.target.value })}
                data-testid="new-customer-zip"
                placeholder="ZIP"
                className={`${inputClass} col-span-2`}
              />
            </div>
          </section>

          {/* Membership + tags */}
          <section className="space-y-3 border-t border-slate-100 pt-4">
            <div>
              <label className={`${labelClass} flex items-center gap-1.5`}>
                <Star className="h-3.5 w-3.5" /> Membership tier
              </label>
              <div className="flex flex-wrap gap-2">
                {MEMBERSHIPS.map((m) => (
                  <button
                    key={m || "none"}
                    type="button"
                    onClick={() => set({ membership: m })}
                    data-testid={`new-customer-membership-${m ? m.toLowerCase() : "none"}`}
                    className={`h-9 px-3 rounded-lg border text-[12.5px] font-medium transition-colors ${
                      f.membership === m
                        ? m
                          ? MEMBERSHIP_STYLE[m]
                          : "border-emerald-400 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {m || "None"}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={`${labelClass} flex items-center gap-1.5`}>
                <TagIcon className="h-3.5 w-3.5" /> Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tagOptions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    data-testid={`new-customer-tag-${t}`}
                    className={`h-8 px-2.5 rounded-full border text-[12px] font-medium transition-colors ${
                      tags.includes(t)
                        ? "border-blue-400 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {t}
                  </button>
                ))}
                {tags
                  .filter((t) => !tagOptions.includes(t))
                  .map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      className="h-8 px-2.5 rounded-full border border-blue-400 bg-blue-50 text-blue-700 text-[12px] font-medium"
                    >
                      {t}
                    </button>
                  ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                  data-testid="new-customer-tag-input"
                  placeholder="Add a custom tag"
                  className={`${inputClass} h-9 flex-1`}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCustomTag}
                  data-testid="new-customer-tag-add"
                  className="h-9 text-[12.5px] shrink-0"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>
          </section>

          {/* Initial note (create only) */}
          {!isEdit && (
            <section className="space-y-2 border-t border-slate-100 pt-4">
              <label className={`${labelClass} flex items-center gap-1.5`}>
                <StickyNote className="h-3.5 w-3.5" /> Initial note
              </label>
              <textarea
                value={f.note}
                onChange={(e) => set({ note: e.target.value })}
                data-testid="new-customer-note"
                rows={3}
                placeholder="Optional — gate code, preferred contact time, special instructions…"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13.5px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
            </section>
          )}

          {/* Duplicate warning */}
          {duplicate && (
            <div
              data-testid="new-customer-dup-warning"
              className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2.5 text-[12.5px] text-amber-800 flex items-start gap-2"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                A similar customer already exists: <strong>{duplicate.name}</strong>
                {duplicate.phone ? ` · ${duplicate.phone}` : ""}. You can still save this profile.
              </span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
          <Button type="button" variant="outline" onClick={onClose} className="h-10 text-[13px]">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="new-customer-save"
            className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : isEdit ? (
              <Pencil className="h-4 w-4 mr-1.5" />
            ) : (
              <UserPlus className="h-4 w-4 mr-1.5" />
            )}
            {isEdit ? "Save changes" : "Create customer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
